import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { fetchEmployees } from '../../../../store/slices/employeeSlice';
import { useSnackbar } from 'notistack';
import { fetchAllProjects } from '../../../../store/slices/projectsSlice';
import { assignProjectToOP } from '../../../../store/slices/managerProjectAssign';
import { setProjectReadTrue } from '../../../../store/slices/readSlice';
import useToolGuideSync from '../../../../features/toolGuide/useToolGuideSync';
import { MANAGER_BUCKET_KEYS } from '../../../../features/toolGuide/toolGuideConfig';

const ToAssignResources = ({
    index,
    row,
    setIsLoading,
    matchedRowEntries,
    matchedRowDesctiption,
    matchedRowHardwareEntries,
    setFilteredCancelEmployee,
    filteredCancelEmployee,
    refreshAssign,
    isViewOnly = false
}) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { syncToolGuideStep } = useToolGuideSync(
        MANAGER_BUCKET_KEYS.PROJECT_ASSIGNED,
        "L4_PM"
    );
    const { employees } = useSelector((state) => state.employees);

    useEffect(() => {
        dispatch(fetchEmployees());
    }, [dispatch]);

    const [wasModified, setWasModified] = useState(false);
    const [assignedPeopleList, setAssignedPeopleList] = useState([]);
    const [employeeParentWithTasks, setEmployeeParentWithTasks] = useState({});
    const [filteredEmployee, setFilteredEmployee] = useState([]);
    const [matchedManager, setMatchedManager] = useState([]);

    // Create refs for the select element
    const selectRef = useRef(null);
    const isProcessingRef = useRef(false);

    // Extract handleForward outside useEffect to avoid dependency issues
    const handleForward = useCallback(async (selectedAssignedPeople) => {
        // Prevent multiple calls
        if (isProcessingRef.current) return;

        if (!selectedAssignedPeople || selectedAssignedPeople.length === 0) {
            enqueueSnackbar("Please Select employee", {
                variant: "error",
            });
            return;
        }

        const payload = {
            projectId: row._id,
            newTaskOwnerId: selectedAssignedPeople[0]._id,
        };
        const projectId = row._id;

        setIsLoading(true);
        isProcessingRef.current = true;

        try {
            await dispatch(assignProjectToOP(payload)).unwrap();
            await dispatch(setProjectReadTrue(projectId));
            enqueueSnackbar("Project Forwarded Successfully", {
                variant: "success",
            });
        } catch (error) {
            console.error("Failed to create task:", error);
            enqueueSnackbar("Failed to forward project", {
                variant: "error"
            });
        } finally {
            dispatch(fetchAllProjects());
            setIsLoading(false);
            setAssignedPeopleList([]);
            setWasModified(false);
            refreshAssign();

            // Reset processing flag after a short delay
            setTimeout(() => {
                isProcessingRef.current = false;
            }, 500);
        }
    }, [row._id, enqueueSnackbar, dispatch, setIsLoading, refreshAssign]);

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = async (event) => {
            // If nothing was modified, just return
            if (!wasModified) return;

            // If click was inside the select or its parent, do nothing
            if (selectRef.current && selectRef.current.contains(event.target)) {
                return;
            }

            // If we have selected employees, call handleForward
            if (assignedPeopleList.length > 0) {
                await handleForward(assignedPeopleList);
            }

            // Reset state
            setWasModified(false);
        };

        // Add event listener with capture phase to catch events properly
        document.addEventListener('mousedown', handleClickOutside, true);
        document.addEventListener('touchstart', handleClickOutside, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside, true);
            document.removeEventListener('touchstart', handleClickOutside, true);
        };
    }, [wasModified, assignedPeopleList, handleForward]);

    // Update employeeParentWithTasks
    useEffect(() => {
        const isExcluded = (emp) => {
            return row?.Assignedperson?.some((person) => {
                if (emp.customRoles?.[0] === "project manager") {
                    return person.email === emp.EmpEmail;
                }
                return (
                    person.projecttracktaskStatus === "submit" &&
                    person.email === emp.EmpEmail
                );
            });
        };

        const merged = employees
            .map((emp) => {
                if (isExcluded(emp)) return null;
                const workEntries = matchedRowEntries.filter(
                    (entry) =>
                        entry.taskownerid === emp?._id
                        && entry.isProjectAssigned === false
                );
                const descriptions = matchedRowDesctiption.filter(
                    (entry) =>
                        entry.taskownerid === emp?._id
                        && entry.isProjectAssigned === false
                );
                const hardwareItems = matchedRowHardwareEntries.filter(
                    (entry) => entry.taskownerid === emp?._id
                );

                if (workEntries.length > 0 || descriptions.length > 0) {
                    return {
                        projectid: row?._id,
                        name: `${emp.EmpFirstName} ${emp.EmpLastName}`,
                        email: emp.EmpEmail,
                        empid: emp._id,
                        role: emp.customRoles?.[0] || "—",
                        TypeService: emp.servicetype,
                        descriptionList: descriptions,
                        hardwareItems: hardwareItems,
                        workEntries,
                    };
                }

                return null;
            })
            .filter(Boolean);

        const finalData = {
            Assignedperson: merged,
            ProjectName: row?.ProjectName,
            TypeService: row?.TypeService,
            _id: row?._id,
        };

        setEmployeeParentWithTasks(finalData);
    }, [
        employees,
        matchedRowDesctiption,
        matchedRowEntries,
        row,
        matchedRowHardwareEntries,
    ]);

    // Update matchedManager
    useEffect(() => {
        const matchedEmployees = employees?.filter((emp) =>
            row.AssignedManagerEmail.includes(emp.EmpEmail)
        );
        setMatchedManager(matchedEmployees);
    }, [employees, row]);

    // Update filteredEmployee
    useEffect(() => {
        if (row && row.Assignedperson && employees?.length > 0) {
            const filteredEmployees = employees.filter(emp => {
                const isAssigned = (employeeParentWithTasks?.Assignedperson || []).some(
                    person => person.email === emp.EmpEmail
                );
                return !isAssigned;
            });

            const assignedEmails = row.Assignedperson.map((p) => p.email);
            const unassignedEmployees = filteredEmployees?.filter(
                (emp) => !assignedEmails.includes(emp.EmpEmail)
            );

            const unassignedWithOperationsRole = unassignedEmployees.filter((emp) =>
                emp.customRoles?.includes("operations")
            );

            const managerServiceTypes = matchedManager.map(m => m.servicetype);
            const filteredByServiceType = unassignedWithOperationsRole.filter(emp =>
                managerServiceTypes.includes(emp.servicetype)
            );

            setFilteredEmployee(filteredByServiceType);
            setFilteredCancelEmployee(filteredByServiceType);
        }
    }, [row, employees, matchedManager, employeeParentWithTasks, setFilteredCancelEmployee]);

    // Handle select change
    const handleSelectChange = (e) => {
        if (isViewOnly) return;
        syncToolGuideStep("assign-employees");
        const selectedEmail = e.target.value;

        if (!selectedEmail) {
            setAssignedPeopleList([]);
            setWasModified(false);
            return;
        }

        const selectedEmp = filteredEmployee.find(
            (emp) => emp.EmpEmail === selectedEmail
        );

        if (selectedEmp) {
            const fullName = `${selectedEmp.EmpFirstName} ${selectedEmp.EmpLastName}`;
            const newAssignedPerson = {
                _id: selectedEmp._id,
                name: fullName,
                email: selectedEmp.EmpEmail,
            };

            setAssignedPeopleList([newAssignedPerson]);
            setWasModified(true);

            // Optional: Auto-submit immediately on selection
            // handleForward([newAssignedPerson]);
        }
    };

    const allCompleteProject = row?.Assignedperson?.every(
        (person) =>
            (person?.manageraction === "complete" ||
                person?.operationaction === "complete") &&
            person?.projecttracktaskStatus === "submit" &&
            person?.taskStatus === "submit"
    );

    return (
        <div className="w-full flex justify-center items-center pt-1">
            <div className="w-full p-1">
                <select
                    ref={selectRef}
                    disabled={isViewOnly || allCompleteProject}
                    className={`w-full border p-2 rounded-md shadow-md text-xs ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-white"}`}
                    value={assignedPeopleList.length > 0 ? assignedPeopleList[0].email : ""}
                    onChange={handleSelectChange}
                    onBlur={() => {
                        // Add a small delay to allow click outside handler to work first
                        setTimeout(() => {
                            if (wasModified && assignedPeopleList.length > 0) {
                                handleForward(assignedPeopleList);
                            }
                        }, 100);
                    }}
                >
                    <option hidden value="">
                     Resource
                    </option>
                    {filteredEmployee.length === 0 && (
                        <option disabled value="no-resources">
                            No resources found
                        </option>
                    )}
                    {filteredEmployee.map((emp) => {
                        const fullName = `${emp.EmpFirstName} ${emp.EmpLastName}`;
                        return (
                            <option key={emp._id} value={emp.EmpEmail}>
                                {fullName}
                            </option>
                        );
                    })}
                </select>
            </div>
        </div>
    );
};

export default ToAssignResources;